import React, { useContext, useMemo, useState } from 'react';
import Row from '../Common/Row';
import OmniSearch from '../OmniSearch';
import FilterPlates from '../FilterPlates';
import Inputs from '../Inputs';
import BonusPeriodsContext from '../../contexts/BonusPeriodsContext';

import Grid from './Grid';
import Settings from '../CustomerLaneCommitsBoard/Settings';
import QuickFilters from '../CustomerLaneCommitsBoard/QuickFilters';
import SignOff from './BonusSignOff';
import type { GridApi } from 'ag-grid-community';
import PeriodSelector from './PeriodSelector';
import ExportButton from '../Grid/ExportButton';
import { SALES_COMMISSION_ACTIONS_ELEMENT_ID } from './constants';
import Typography from 'kingpin/atoms/Typography';
import {
  BoardFilterBar,
  BoardMainContent,
  BoardQuickFilterBounds,
  BoardTopBar,
  BoardWrapper,
} from 'components/CustomerLaneCommitsBoard/styles';
import BoardContext from '../../contexts/BoardContext';
import WorkSpaceContext from '../../contexts/WorkSpaceContext';
import { BoardFreshnessTooltip } from '../DatasetFreshness';
import isGeneralBoard from '../../isBoardWithSlideOut';
import V3ContentBackButton from '../../screens/V3/Content/V3ContentBackButton';
import V3PublishOrShareButton from '../../screens/V3/Content/V3PublishOrShareButton';

const BonusPeriodPicker = () => {
  const { bonusPeriods, selectedBonusPeriod, setSelectedBonusPeriod } =
    useContext(BonusPeriodsContext);

  const options = useMemo((): DropdownOption[] => {
    return bonusPeriods.map((p) => ({
      key: p.id,
      label: p.label,
      isSelected: selectedBonusPeriod ? selectedBonusPeriod.id === p.id : false,
      onSelected: () => setSelectedBonusPeriod(p),
    }));
  }, [bonusPeriods, selectedBonusPeriod, setSelectedBonusPeriod]);

  return <Inputs.Dropdown options={options} />;
};

const Board = ({
  board,
  sortOverride,
  onQuickFilterChanged,
  onManualFilterChanged,
  gridApi,
  setGridApi,
  exportHandle,
  setExportHandle,
  isBonusPeriodDriven,
  isPeriodSelectorForced,
}: {
  board: DriverPerformanceBoard | GeneralBoard;
  sortOverride?: SimpleGridSort[];
  onQuickFilterChanged: () => void;
  onManualFilterChanged: () => void;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  // Set when DataTable draws the grid, which the export reads in place of
  // the grid's api
  exportHandle?: DataTable.ExportHandle;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
  isBonusPeriodDriven?: boolean;
  isPeriodSelectorForced?: boolean;
}) => {
  const { quickFilters } = useContext(BoardContext);
  const { workSpace } = useContext(WorkSpaceContext);
  const isWorkSpace = !!workSpace;
  const hasQuickFilters = quickFilters.length > 0;
  const [dataTypes] = useState<string[]>([board.dataType]);

  return (
    <BoardWrapper>
      <BoardTopBar>
        <Row centerAlign style={{ gap: 8 }}>
          <V3ContentBackButton />
          <Typography.Header type="H5">{board.name}</Typography.Header>
          {isGeneralBoard(board) && <BoardFreshnessTooltip board={board} />}
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          <V3PublishOrShareButton />
          {isBonusPeriodDriven && <BonusPeriodPicker />}
          <ExportButton
            overrideGridApi={exportHandle ? undefined : gridApi}
            exportHandle={exportHandle}
            exportName={board.name}
          />
          <PeriodSelector
            isWorkSpaceTopBar={false}
            isForced={isPeriodSelectorForced}
          />
          <SignOff />
          <Settings />
        </Row>
      </BoardTopBar>
      <BoardFilterBar hasBorder={hasQuickFilters}>
        <Row centerAlign style={{ gap: 8 }}>
          <div style={{ width: 150 }}>
            <OmniSearch.DrillDownBuilder
              dataTypes={dataTypes}
              onManualFilterChanged={onManualFilterChanged}
            />
          </div>
          <FilterPlates onManualFilterChanged={onManualFilterChanged} />
        </Row>
        <div id={SALES_COMMISSION_ACTIONS_ELEMENT_ID} />
      </BoardFilterBar>
      {hasQuickFilters && (
        <BoardFilterBar>
          <BoardQuickFilterBounds>
            <QuickFilters onQuickFilterChanged={onQuickFilterChanged} />
          </BoardQuickFilterBounds>
        </BoardFilterBar>
      )}
      <BoardMainContent
        isWorkSpace={isWorkSpace}
        hasQuickFilters={hasQuickFilters}
      >
        <Grid
          board={board}
          sortOverride={sortOverride}
          gridApi={gridApi}
          setGridApi={setGridApi}
          setExportHandle={setExportHandle}
        />
      </BoardMainContent>
    </BoardWrapper>
  );
};

export default Board;
